<template>
    <div class="tts-projects-page">
        <InputPopup v-model="showInputPopup" :title="popupTitle" :fields="popupFields" @confirm="onInputPopupConfirm" />
        <InfoPopup v-model="showInfoPopup" title="Warning!" :showConfirmButton="showInfoPopupConfirmButton"
            :showCancelButton="showInfoPopupCancelButton" @confirm="onInfoPopupConfirm">
            <div>{{ infoPopupMessage }}</div>
        </InfoPopup>
        <div v-if="isLoading" class="loading-overlay">
            <div class="spinner"></div>
        </div>
        <div class="content-shell">
            <section class="hero" aria-labelledby="projects-title">
                <div>
                    <h3 id="projects-title">Text-to-Speech Studio</h3>
                    <p>Organize your work into projects and chapters, then generate lifelike audio.</p>
                </div>
            </section>

            <section class="stats-grid" aria-label="Project overview">
                <article class="stat-card">
                    <span>Projects</span>
                    <strong>{{ visibleProjects.length }}</strong>
                    <p>Active production folders</p>
                </article>
                <article class="stat-card">
                    <span>Latest update</span>
                    <strong>{{ lastModifiedLabel }}</strong>
                    <p>Most recently changed project</p>
                </article>
                <article class="stat-card voice-modes">
                    <span>Voices</span>
                    <strong>2 modes</strong>
                    <p>Basic and Pro available</p>
                </article>
            </section>

            <div class="workspace-grid">
                <section class="panel project-library" aria-labelledby="project-library-title">
                    <div class="panel-head">
                        <div>
                            <div id="project-library-title" class="section-title" role="heading" aria-level="2">
                                Project library
                            </div>
                            <p>Search, sort, create, rename, and open text-to-speech projects.</p>
                        </div>
                    </div>

                    <div class="toolbar">
                        <label class="search-box">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                aria-hidden="true">
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.35-4.35" />
                            </svg>
                            <input v-model="searchTerm" type="search" placeholder="Search projects..."
                                aria-label="Search projects" />
                        </label>

                        <DropdownList v-model="sortOption" label="Sort by" :items="[
                            { label: 'Relevance', value: 'relevance' },
                            { label: 'Last Modified', value: 'last_modified' }
                        ]" class="sort-select" />
                    </div>

                    <div class="projects-grid">
                        <div v-if="sortedProjects.length === 0" class="empty-state">
                            <div class="empty-state-content">
                                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="1.5" class="empty-icon">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14,2 14,8 20,8" />
                                    <line x1="16" y1="13" x2="8" y2="13" />
                                    <line x1="16" y1="17" x2="8" y2="17" />
                                    <polyline points="10,9 9,9 8,9" />
                                </svg>
                                <h3 class="empty-title">No projects found</h3>
                                <p class="empty-description">
                                    Login to create new projects and start generating lifelike audio.
                                </p>
                            </div>
                        </div>

                        <article v-for="project in sortedProjects" :key="project.project_id"
                            class="project-card" role="button" tabindex="0" @click="goToProject(project)"
                            @keydown.enter.prevent="goToProject(project)"
                            @keydown.space.prevent="goToProject(project)">
                            <div class="project-top">
                                <span class="project-icon">{{ projectInitial(project.project_name) }}</span>
                                <div class="card-menu" @click.stop>
                                    <button class="menu-btn" type="button" aria-label="Project actions"
                                        :aria-expanded="openMenuId === project.project_id"
                                        @click="toggleMenu(project.project_id)">
                                        <span>...</span>
                                    </button>
                                    <ul v-if="openMenuId === project.project_id" class="menu-dropdown">
                                        <li><button type="button" @click="renameProject(project)">Rename</button></li>
                                        <li><button type="button"
                                                @click="onRemoveProject(userId, project.project_id)">Delete</button>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div class="card-body">
                                <h3 class="card-title">{{ project.project_name }}</h3>
                                <p>Last modified {{ formatDateWithTime(project.updated_time) }}</p>
                            </div>
                        </article>
                    </div>
                </section>

                <aside class="quick-actions">
                    <section class="panel quick-card" aria-label="Project creation">
                        <button class="action-btn primary-action" type="button" @click="addNewProject">
                            Create blank project
                        </button>
                    </section>
                </aside>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { createProject, getProjectsByUserId, modifyProjectName, deleteProject } from '@/api/ttsProjects';
import { requireAuth, checkAuthentication } from '@/api/auth';
import DropdownList from '@/components/common/DropdownList.vue';
import { checkInput } from '@/utils/InputChecker';
import InputPopup from '@/components/common/InputPopup.vue';
import InfoPopup from '@/components/common/InfoPopup.vue';

const router = useRouter();
var userId = 'userId';
const projects = ref([]);
const searchTerm = ref('');
const sortOption = ref('relevance');
const isLoading = ref(false);
const openMenuId = ref(null);

const showInputPopup = ref(false);
const popupTitle = ref('');
const popupFields = ref([]);
let popupAction = null;
let popupProject = null;

const showInfoPopup = ref(false);
const infoPopupMessage = ref('');
const infoPopupNextAction = ref('');
const infoPopupNextTargetProjectId = ref('');
const showInfoPopupConfirmButton = ref(false);
const showInfoPopupCancelButton = ref(true);

onMounted(async () => {
    await fetchProjects();
});

async function fetchProjects() {
    isLoading.value = true;
    try {
        const authStatus = checkAuthentication();

        if (!authStatus.isAuthenticated) {
            projects.value = [];
            console.log('User not authenticated, clearing projects');
            return;
        }

        userId = authStatus.userId;
        const data = await getProjectsByUserId();
        console.log('Fetched projects payload:', data);
        let list = data;
        if (typeof list === 'string') {
            try {
                list = JSON.parse(list);
            } catch (e) {
                console.warn('Failed to parse projects JSON:', e);
            }
        }
        if (!Array.isArray(list)) {
            list = list.projects ?? list.data ?? [];
        }
        projects.value = list.filter(p => p.status !== 'deleted');

        projects.value.forEach(va => {
            console.log(va.status)
        });
    } catch (err) {
        console.error('Failed to fetch data', err);
        projects.value = [];
    } finally {
        isLoading.value = false;
    }
}

const visibleProjects = computed(() => projects.value.filter(p => p.status !== 'deleted'));

const filteredProjects = computed(() => {
    if (!searchTerm.value) return visibleProjects.value;
    const term = searchTerm.value.toLowerCase();
    return visibleProjects.value.filter(p =>
        String(p.project_name || '').toLowerCase().includes(term)
    );
});

const sortedProjects = computed(() => {
    const list = filteredProjects.value.slice();
    if (sortOption.value === 'last_modified') {
        return list.sort(
            (a, b) => projectTime(b.updated_time) - projectTime(a.updated_time)
        );
    }
    if (searchTerm.value) {
        const term = searchTerm.value.toLowerCase();
        return list.sort((a, b) => {
            const ai = String(a.project_name || '').toLowerCase().indexOf(term);
            const bi = String(b.project_name || '').toLowerCase().indexOf(term);
            return ai - bi;
        });
    }
    return list;
});

const lastModifiedProject = computed(() => {
    return visibleProjects.value.reduce((latest, project) => {
        if (!latest) return project;
        return projectTime(project.updated_time) > projectTime(latest.updated_time) ? project : latest;
    }, null);
});

const lastModifiedLabel = computed(() => {
    if (!lastModifiedProject.value) return 'None';
    const dt = new Date(projectTime(lastModifiedProject.value.updated_time));
    return dt.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric'
    });
});

async function addNewProject() {
    if (!(await requireAuth('TTsProjects.addNewProject'))) return;
    popupTitle.value = 'Enter name for the new project';
    popupFields.value = [{ key: 'name', label: 'Project Name', default: '' }];
    popupAction = 'add';
    showInputPopup.value = true;
}

async function onInputPopupConfirm(values) {
    const name = values.name;
    if (!name || !name.trim()) return;

    const nameLengthLimit = 30;
    const checkName = checkInput(name, nameLengthLimit);
    if (!checkName.isLongEnough) {
        infoPopupMessage.value = `Name is too long (${name.length} / ${nameLengthLimit} characters). Please shorten it.`;
        showInfoPopupConfirmButton.value = true;
        showInfoPopupCancelButton.value = false;
        showInfoPopup.value = true;
        return;
    }
    if (!checkName.hasOnlyAllowed) {
        infoPopupMessage.value = "Name contains invalid characters: " + checkName.disallowedChars.join(' ') + ". Only English letters, numbers, and common punctuation are allowed.";
        showInfoPopupConfirmButton.value = true;
        showInfoPopupCancelButton.value = false;
        showInfoPopup.value = true;
        return;
    }

    isLoading.value = true;
    try {
        if (popupAction === 'add') {
            const { project_id } = await createProject();
            await modifyProjectName(project_id, name.trim());
            await fetchProjects();
        } else if (popupAction === 'rename' && popupProject) {
            if (name.trim() === popupProject.project_name) return;
            await modifyProjectName(popupProject.project_id, name.trim());
            popupProject.project_name = name.trim();
        }
    } catch (err) {
        infoPopupMessage.value = "Failed to create/rename project. Please try again.";
        showInfoPopupConfirmButton.value = true;
        showInfoPopupCancelButton.value = false;
        showInfoPopup.value = true;
    } finally {
        isLoading.value = false;
        popupAction = null;
        popupProject = null;
    }
}
async function renameProject(project) {
    popupTitle.value = 'Enter new project name';
    popupFields.value = [{ key: 'name', label: 'Project Name', default: project.project_name }];
    popupAction = 'rename';
    popupProject = project;
    showInputPopup.value = true;
}

async function onInfoPopupConfirm() {
    showInfoPopup.value = false;
    if (infoPopupNextAction.value === 'delete') {
        isLoading.value = true;
        try {
            const _response = await deleteProject(infoPopupNextTargetProjectId.value);
            projects.value = projects.value.filter(p => p.project_id !== infoPopupNextTargetProjectId.value);
            infoPopupNextAction.value = '';
            infoPopupNextTargetProjectId.value = '';
        } catch (err) {
            console.error('Failed to remove project', err);
            infoPopupMessage.value = 'Failed to remove project: ' + err;
            infoPopupNextAction.value = '';
            showInfoPopupConfirmButton.value = true;
            showInfoPopupCancelButton.value = false;
            showInfoPopup.value = true
            infoPopupNextTargetProjectId.value = '';
        } finally {
            isLoading.value = false;
        }
    }
}

function onRemoveProject(_userId, projectId) {
    infoPopupMessage.value = 'Do you really want to delete the entire project and its content?'
    infoPopupNextAction.value = 'delete';
    showInfoPopupConfirmButton.value = true;
    showInfoPopupCancelButton.value = true;
    showInfoPopup.value = true
    infoPopupNextTargetProjectId.value = projectId;
}

function goToProject(project) {
    router.push({
        path: '/text-to-speech',
        query: {
            project: encodeURIComponent(JSON.stringify(project))
        }
    });
}
function toggleMenu(id) {
    openMenuId.value = openMenuId.value === id ? null : id;
}

function projectInitial(name = '') {
    return String(name).trim().charAt(0).toUpperCase() || 'T';
}

function projectTime(time) {
    if (typeof time === 'number') return time * 1000;
    const value = new Date(time).getTime();
    return Number.isNaN(value) ? 0 : value;
}

function formatDateWithTime(time) {
    const dt = new Date(projectTime(time));
    const date = dt.toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric'
    });
    const timeStr = dt.toLocaleTimeString(undefined, {
        hour: 'numeric', minute: '2-digit', second: '2-digit'
    });
    return `${date} ${timeStr}`;
}

function handleClickOutside(event) {
    if (openMenuId.value && !event.target.closest('.card-menu')) {
        openMenuId.value = null;
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.tts-projects-page {
    min-height: 100%;
    padding: 24px 28px 36px;
    color: #111827;
    background: #f7f8fc;
}

.content-shell {
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;
    display: grid;
    gap: 16px;
}

.hero,
.stat-card,
.panel,
.project-card {
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 10px 34px rgba(17, 24, 39, 0.06);
}

.hero {
    padding: 22px;
    background: #fff;
    box-shadow: 0 22px 70px rgba(17, 24, 39, 0.08);
}

.hero h3 {
    margin: 0 0 6px;
    color: #0f172a;
    font-size: 24px;
    font-weight: 760;
    line-height: 1.15;
    text-align: left;
}

.hero p,
.panel-head p,
.project-card p,
.stat-card p {
    margin: 0;
    color: #5f6b7d;
    line-height: 1.5;
}

.hero p {
    max-width: 720px;
    font-size: 17px;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
}

.stat-card {
    min-width: 0;
    padding: 15px;
}

.stat-card span {
    display: block;
    color: #5f6b7d;
    font-size: 12px;
    font-weight: 740;
    letter-spacing: 0.05em;
    text-transform: uppercase;
}

.stat-card strong {
    display: block;
    margin-top: 8px;
    color: #0f172a;
    font-size: 25px;
    line-height: 1;
}

.stat-card p {
    margin-top: 5px;
    font-size: 13px;
}

.workspace-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 330px;
    gap: 20px;
    align-items: start;
}

.panel {
    padding: 18px;
}

.panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
}

.panel-head p {
    margin-top: 6px;
}

.section-title {
    margin: 0;
    color: #0f172a;
    font-size: 19px;
    font-weight: 760;
    line-height: 1.2;
    text-align: left;
}

.toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
}

.search-box {
    min-height: 44px;
    flex: 1 1 260px;
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr);
    align-items: center;
    gap: 10px;
    padding: 0 12px;
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: #fff;
}

.search-box svg {
    width: 18px;
    height: 18px;
    color: #64748b;
}

.search-box input {
    width: 100%;
    border: 0;
    outline: 0;
    color: #111827;
    background: transparent;
    font: inherit;
}

.sort-select {
    min-height: 44px;
}

.projects-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-top: 14px;
}

.project-card {
    min-width: 0;
    display: grid;
    gap: 14px;
    padding: 14px;
    cursor: pointer;
    transition: background 180ms ease, border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
}

.project-card:hover,
.project-card:focus-visible {
    border-color: #c4b5fd;
    background: #fff;
    box-shadow: 0 16px 38px rgba(17, 24, 39, 0.09);
    transform: translateY(-2px);
}

.project-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}

.project-icon {
    width: 44px;
    height: 44px;
    display: grid;
    place-items: center;
    border-radius: 8px;
    color: #6d28d9;
    background: #f3f0ff;
    font-weight: 800;
}

.card-menu {
    position: relative;
    z-index: 2;
}

.menu-btn {
    width: 36px;
    height: 36px;
    display: grid;
    place-items: center;
    border: 1px solid #dfe5ef;
    border-radius: 999px;
    color: #475569;
    background: #fff;
    cursor: pointer;
    font-size: 17px;
    font-weight: 800;
    line-height: 1;
    transition: background 180ms ease, border-color 180ms ease, color 180ms ease;
}

.menu-btn:hover,
.menu-btn:focus-visible {
    border-color: #c4b5fd;
    color: var(--purple, #7c3aed);
    background: #f3f0ff;
}

.menu-dropdown {
    position: absolute;
    right: 0;
    top: 42px;
    min-width: 128px;
    overflow: hidden;
    padding: 4px;
    margin: 0;
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 16px 38px rgba(17, 24, 39, 0.12);
    list-style: none;
}

.menu-dropdown button {
    width: 100%;
    min-height: 34px;
    padding: 7px 10px;
    border: 0;
    border-radius: 6px;
    color: #243044;
    background: transparent;
    cursor: pointer;
    font: inherit;
    text-align: left;
}

.menu-dropdown button:hover,
.menu-dropdown button:focus-visible {
    color: var(--purple, #7c3aed);
    background: #f3f0ff;
}

.card-body {
    min-width: 0;
    display: grid;
    gap: 8px;
}

.card-title {
    margin: 0;
    color: #0f172a;
    font-size: 18px;
    font-weight: 760;
    line-height: 1.2;
    text-align: left;
    word-break: break-word;
}

.quick-actions {
    position: sticky;
    top: 24px;
    display: grid;
    gap: 16px;
}

.quick-card {
    display: grid;
    gap: 12px;
}

.action-btn {
    min-height: 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 14px;
    border: 1px solid #dfe5ef;
    border-radius: 7px;
    color: #243044;
    background: rgba(255, 255, 255, 0.88);
    cursor: pointer;
    font: inherit;
    font-weight: 700;
    transition: background 180ms ease, border-color 180ms ease, color 180ms ease, transform 180ms ease;
}

.action-btn:hover,
.action-btn:focus-visible {
    border-color: #c4b5fd;
    color: var(--purple, #7c3aed);
    background: #f3f0ff;
    transform: translateY(-1px);
}

.primary-action {
    border-color: transparent;
    color: #fff;
    background: var(--purple, #7c3aed);
    box-shadow: 0 12px 28px rgba(124, 58, 237, 0.16);
}

.primary-action:hover,
.primary-action:focus-visible {
    color: #fff;
    background: var(--purple-hover, #6d28d9);
}

.empty-state {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    text-align: center;
}

.empty-state-content {
    max-width: 430px;
    padding: 32px;
}

.empty-icon {
    color: #94a3b8;
    margin-bottom: 16px;
}

.empty-title {
    margin: 0 0 12px;
    color: #0f172a;
    font-size: 22px;
    font-weight: 760;
}

.empty-description {
    margin: 0;
    color: #5f6b7d;
    font-size: 16px;
    line-height: 1.5;
}

.loading-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(2px);
}

.spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e2e8f0;
    border-top: 4px solid var(--purple, #7c3aed);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

button:focus-visible,
[role="button"]:focus-visible,
input:focus-visible {
    outline: 3px solid rgba(124, 58, 237, 0.24);
    outline-offset: 2px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 1120px) {
    .workspace-grid,
    .stats-grid {
        grid-template-columns: 1fr 1fr;
    }

    .quick-actions {
        position: static;
    }
}

@media (max-width: 760px) {
    .tts-projects-page {
        padding: 16px;
    }

    .hero,
    .panel-head {
        align-items: stretch;
        flex-direction: column;
    }

    .stats-grid,
    .workspace-grid,
    .projects-grid {
        grid-template-columns: 1fr;
    }
}

@media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
        scroll-behavior: auto !important;
        transition-duration: 0.01ms !important;
        animation-duration: 0.01ms !important;
    }
}
</style>
